import { WarningTwoTone } from "@ant-design/icons";
import type { SessionView } from "@p0security/shared/integrations/sessions/types";
import type { PermissionRequest } from "@p0security/shared/types/permission";
import { Alert, Button, Card, Flex, Spin, Typography } from "antd";
import { ExportOption } from "components/Export";
import { GraphTooltip } from "components/GraphTooltip";
import { ChangeEvent } from "react";

import { SessionViewRouter } from "./SessionViewRouter";

const graphTooltipContent = (
  <div>
    <Typography.Text>
      This will incur additional costs upon usage. Please contact P0 support or
      refer to{" "}
      <a href="https://aws.amazon.com/cloudtrail/pricing/">
        AWS CloudTrail pricing
      </a>{" "}
      for more information.
    </Typography.Text>
  </div>
);

export const SessionTable = ({
  view,
  error,
  loading,
  quantity,
  updateQuantity,
  search,
  updateSearch,
  includeResourceSearch,
  updateIncludeResourceSearch,
  getSession,
  exportOptions,
  requestId,
  requestType,
  access,
}: {
  view?: SessionView;
  error: string | undefined;
  loading: boolean;
  quantity: number;
  updateQuantity: (value: number) => void;
  search: string;
  updateSearch: (value: ChangeEvent<HTMLInputElement>) => void;
  includeResourceSearch: boolean;
  updateIncludeResourceSearch: (value: boolean) => void;
  getSession: () => void;
  exportOptions: Record<string, ExportOption<unknown>>;
  requestId: string;
  requestType: PermissionRequest["type"];
  access?: PermissionRequest["access"];
}) => {
  const isAws = requestType === "aws";
  const isSshSession = requestType === "ssh" && access === "session";
  const isSelfHostedComputer =
    requestType === "self-hosted" && access === "computer";
  const isRdpSession = requestType === "rdp" && access === "session";
  const isPostgres = requestType === "pg";
  const isCustom = requestType === "custom";
  const isAuditSession = isPostgres || isCustom;
  const isRecordingSession =
    isSshSession || isSelfHostedComputer || isRdpSession;

  if (!isAws && !isRecordingSession && !isPostgres && !isCustom && !view) {
    // TODO: Enable for other request types
    return null;
  }

  return (
    <Flex style={{ marginTop: "16px" }} vertical gap="middle">
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
        />
      )}
      {view ? (
        <Card style={{ marginTop: "16px" }}>
          <SessionViewRouter
            view={view}
            loading={loading}
            quantity={quantity}
            updateQuantity={updateQuantity}
            search={search}
            updateSearch={updateSearch}
            includeResourceSearch={includeResourceSearch}
            updateIncludeResourceSearch={updateIncludeResourceSearch}
            getSession={getSession}
            exportOptions={exportOptions}
            requestId={requestId}
          />
        </Card>
      ) : (
        <Button
          disabled={loading}
          type="primary"
          onClick={getSession}
          style={{ width: "fit-content" }}
        >
          {loading && <Spin />}
          {loading
            ? "Preparing..."
            : isRecordingSession
              ? "View Session Recordings"
              : isAuditSession
                ? "View Session Audit"
                : "View Session History"}
          {isAws && (
            <GraphTooltip title={graphTooltipContent}>
              <WarningTwoTone style={{ marginLeft: "8px" }} />
            </GraphTooltip>
          )}
        </Button>
      )}
    </Flex>
  );
};
